import { goalHealth, parseEffort, fmtDeadline } from './helpers';
import { ROLES } from './tokens';

// Mirrors the server's pure scoring (scheduler-engine.js → scoreTask). Keep in sync.
// priority_weight: 1=ASAP, 2=High, 3=Medium, else=Low.
function basePriorityScore(task, now) {
  const pw = task.priority === 1 ? 1000 : task.priority === 2 ? 100 : task.priority === 3 ? 10 : 1;
  const dur = task.duration_minutes || parseEffort(task.effort) || 30;
  const slack = task.deadline ? (new Date(task.deadline).getTime() - now.getTime()) / 60000 - dur : Infinity;
  const urgencyBoost = !isFinite(slack) ? 0 : 10000 / Math.max(slack, 1);
  const days = task.created_at ? (now.getTime() - new Date(task.created_at).getTime()) / 86400000 : 0;
  const ageBoost = Math.max(0, Math.floor(days / 7));
  const hardBonus = task.hard_deadline && slack < 1440 ? 500 : 0;
  return { score: pw + urgencyBoost + ageBoost + hardBonus, slack, urgencyBoost, ageBoost, hardBonus };
}

/**
 * Priority Engine — The Chief of Staff Brain
 *
 * Scores every actionable item using multi-factor analysis. Task base score
 * uses the same formula as the server scheduler (priority_weight + urgency_boost
 * + age_boost + hard_bonus). Frontend-only multipliers (goal health, milestone
 * urgency, calendar alignment, role balance) layer on top so the NOW card
 * reflects context the scheduler doesn't see.
 *
 * Returns ranked stack. #1 item powers the NOW card.
 * Each item includes scoreBreakdown for transparency.
 */
export function computePriorityStack({ tasks, goals, milestones, calendarEvents, inbox }) {
  const items = [];
  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();

  // Track P1 roles for cross-venture conflict detection
  const rolesWithP1 = new Set();
  const openTasks = tasks.filter(t => !t.done);
  openTasks.forEach(t => {
    if (t.priority === 1) {
      const roleSlug = t.roles?.slug || t.goals?.roles?.slug;
      if (roleSlug) rolesWithP1.add(roleSlug);
    }
  });
  const hasCompetingP1 = rolesWithP1.size > 1;

  // Score tasks
  openTasks.forEach(task => {
    const effort = parseEffort(task.effort);
    const dueLabel = task.due_label || fmtDeadline(task.deadline);
    const baseInfo = basePriorityScore(task, now);
    const base = baseInfo.score;
    let score = base;

    // Track multipliers for breakdown
    let goalHealthMultiplier = 1;
    let goalPriorityMultiplier = 1;
    let milestoneBoost = 1;
    let calendarBoost = false;
    let quickWinBonus = 0;
    let roleBalancePenalty = false;

    // ─── Goal health multiplier ─────────────────────────────────────────
    const goalId = task.goal_id;
    let goal = null;
    if (goalId) {
      goal = goals.find(g => g.id === goalId);
      if (goal) {
        const health = goalHealth(goal, milestones);
        if (health === 'at-risk') goalHealthMultiplier = 1.8;
        else if (health === 'drifting') goalHealthMultiplier = 1.3;
        score *= goalHealthMultiplier;

        // ─── Goal priority cascade ──────────────────────────────────────
        const gp = goal.priority || 3;
        if (gp === 1) goalPriorityMultiplier = 2.0;       // Critical
        else if (gp === 2) goalPriorityMultiplier = 1.5;   // High
        else if (gp === 3) goalPriorityMultiplier = 1.0;   // Medium
        else goalPriorityMultiplier = 0.7;                  // Low
        score *= goalPriorityMultiplier;

        // ─── Milestone dependency boost ─────────────────────────────────
        const goalMs = milestones.filter(m => m.goal_id === goal.id && !m.done);
        const nextMs = goalMs.sort((a, b) => a.sort_order - b.sort_order)[0];
        if (nextMs?.target_date) {
          const msDays = Math.ceil((new Date(nextMs.target_date) - now) / 86400000);
          if (msDays < 0) { milestoneBoost = 1.6; score *= 1.6; }        // Milestone overdue
          else if (msDays <= 3) { milestoneBoost = 1.4; score *= 1.4; }   // Due within 3 days
          else if (msDays <= 7) { milestoneBoost = 1.15; score *= 1.15; } // Due within week
        }
      }
    }

    // ─── Calendar alignment ─────────────────────────────────────────────
    const taskRole = task.roles?.slug || task.goals?.roles?.slug;
    if (taskRole && calendarEvents.length > 0) {
      const hasRelatedEvent = calendarEvents.some(e => e.role?.toLowerCase() === taskRole);
      if (hasRelatedEvent) { calendarBoost = true; score *= 1.2; }
    }

    // ─── Quick win bonus ────────────────────────────────────────────────
    if (effort <= 30) { quickWinBonus = 100; score += 100; }

    // ─── Manual queue rank ──────────────────────────────────────────────
    // The user dragged this into an explicit position on the dashboard
    // queue (sort_order >= 1). Their #1 must beat every computed score so
    // the NOW card always agrees with the queue they arranged.
    if (task.sort_order >= 1) score += 10_000_000 / task.sort_order;

    // ─── Cross-venture conflict penalty ─────────────────────────────────
    if (hasCompetingP1 && task.priority === 1) score -= 50;

    // ─── Stale task detection ───────────────────────────────────────────
    const createdAt = new Date(task.created_at || 0);
    const daysSinceCreated = (now - createdAt) / 86400000;
    const isStale = daysSinceCreated > 3 && !task.done_at;

    const roleSlug = task.roles?.slug || task.goals?.roles?.slug;
    const r = roleSlug ? ROLES[roleSlug] : null;

    items.push({
      type: 'task',
      item: task,
      score,
      stale: isStale,
      reason: task.priority === 1 ? 'Urgent priority' :
              goal?.priority === 1 ? 'Critical goal' :
              goal?.priority === 2 ? 'High priority goal' :
              dueLabel === 'Overdue' ? 'Overdue' :
              dueLabel === 'Today' ? 'Due today' :
              milestoneBoost > 1 ? 'Milestone due soon' :
              effort <= 30 ? 'Quick win' : 'Scheduled',
      color: task.priority === 1 ? 'red' :
             goal?.priority === 1 ? 'red' :
             dueLabel === 'Today' || dueLabel === 'Overdue' ? 'amber' : 'blue',
      roleSlug,
      roleColor: r?.color,
      scoreBreakdown: {
        base: Math.round(base),
        urgencyBoost: Math.round(baseInfo.urgencyBoost * 100) / 100,
        ageBoost: baseInfo.ageBoost,
        hardBonus: baseInfo.hardBonus,
        goalHealthMultiplier,
        goalPriorityMultiplier,
        milestoneBoost,
        calendarBoost,
        quickWinBonus,
        competingP1Penalty: hasCompetingP1 && task.priority === 1,
        stale: isStale,
        final: Math.round(score),
      },
    });
  });

  // ─── Role balance penalty ─────────────────────────────────────────────
  const roleCounts = {};
  items.filter(i => i.type === 'task').forEach(i => {
    if (i.roleSlug) roleCounts[i.roleSlug] = (roleCounts[i.roleSlug] || 0) + 1;
  });
  const totalTaskItems = Object.values(roleCounts).reduce((a, b) => a + b, 0);
  if (totalTaskItems > 3) {
    items.filter(i => i.type === 'task').forEach(i => {
      if (i.roleSlug && roleCounts[i.roleSlug] / totalTaskItems > 0.6) {
        i.score *= 0.85;
        i.scoreBreakdown.roleBalancePenalty = true;
        i.scoreBreakdown.final = Math.round(i.score);
      }
    });
  }

  // Score drifting/at-risk goals without open tasks
  goals.filter(g => g.status === 'active').forEach(goal => {
    const health = goalHealth(goal, milestones);
    if (health === 'at-risk' || health === 'drifting') {
      const hasOpenTasks = openTasks.some(t => t.goal_id === goal.id);
      if (!hasOpenTasks) {
        let score = health === 'at-risk' ? 900 : 600;
        // Boost by goal priority
        const gp = goal.priority || 3;
        if (gp === 1) score *= 1.5;
        else if (gp === 2) score *= 1.2;

        const roleSlug = goal.roles?.slug;
        const r = roleSlug ? ROLES[roleSlug] : null;
        items.push({
          type: 'goal_alert',
          item: goal,
          score,
          reason: `Goal is ${health === 'at-risk' ? 'at risk' : 'drifting'} with no open tasks`,
          color: health === 'at-risk' ? 'red' : 'amber',
          roleSlug,
          roleColor: r?.color,
        });
      }
    }
  });

  // Score upcoming meetings requiring prep
  calendarEvents.forEach(event => {
    const [h, m] = event.time.split(':').map(Number);
    const eventMin = h * 60 + m;
    const minsUntil = eventMin - nowMin;

    if (minsUntil > 0 && minsUntil <= 90 && event.hasPREP) {
      const score = 800 + (90 - minsUntil) * 5;
      items.push({
        type: 'meeting_prep',
        item: event,
        score,
        reason: `Prep for meeting in ${minsUntil}min`,
        color: 'violet',
        roleSlug: event.role?.toLowerCase(),
      });
    }
  });

  // Score unread inbox items from today
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const unreadToday = (inbox || []).filter(i =>
    !i.is_read && !i.is_archived && new Date(i.created_at) >= todayStart
  );
  if (unreadToday.length > 0) {
    items.push({
      type: 'inbox_unread',
      item: { count: unreadToday.length, latest: unreadToday[0] },
      score: 400,
      reason: `${unreadToday.length} unread brief${unreadToday.length > 1 ? 's' : ''} today`,
      color: 'blue',
    });
  }

  // Sort by score descending
  items.sort((a, b) => b.score - a.score);
  return items;
}

/**
 * Compute venture balance — hours allocated per role today
 */
export function computeVentureBalance(calendarEvents, scheduledTasks) {
  const balance = {};

  calendarEvents.forEach(event => {
    const role = event.role?.toLowerCase();
    if (!role || !ROLES[role]) return;
    const [h, m] = event.time.split(':').map(Number);
    const startMin = h * 60 + m;
    let endMin = startMin + 60;
    if (event.endTime) {
      const [eh, em] = event.endTime.split(':').map(Number);
      endMin = eh * 60 + em;
    }
    const hours = (endMin - startMin) / 60;
    balance[role] = (balance[role] || 0) + hours;
  });

  scheduledTasks.forEach(block => {
    if (block.type !== 'task') return;
    const task = block.item;
    const role = task.roles?.slug || task.goals?.roles?.slug;
    if (!role || !ROLES[role]) return;
    const hours = (block.endMin - block.startMin) / 60;
    balance[role] = (balance[role] || 0) + hours;
  });

  return Object.entries(ROLES).map(([slug, r]) => ({
    slug,
    label: r.label,
    color: r.color,
    hours: Math.round((balance[slug] || 0) * 10) / 10,
  }));
}
